import { Fragment, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarHeading,
} from '@/layouts/demo1/toolbar';
import { useCurrentUserRoles } from '@/utils/userRole';
import { UserRoles } from '@/constant';

import { Demo1LightSidebarContent } from './';
import { ServiceProviderDashboard } from './ServiceProviderDashboard';
import { MarinaManagerDashboard } from './MarinaManagerDashboard';
import { RegionalOperatorDashboard } from './RegionalOperatorDashboard';

const TEMPLATE_TOOLBAR_DESCRIPTION =
  'Central Hub for Personal Customization';

const getToolbarDescription = (
  isServiceProvider: boolean,
  isMarinaManager: boolean,
  isRegionalOperator: boolean
): string => {
  if (isServiceProvider) {
    return TEMPLATE_TOOLBAR_DESCRIPTION;
  }
  if (isMarinaManager) {
    return 'Overview of your harbors and berth bookings';
  }
  if (isRegionalOperator) {
    return 'Overview of harbors and operations in your region';
  }
  return TEMPLATE_TOOLBAR_DESCRIPTION;
};

const Demo1LightSidebarPage = () => {
  const roles = useCurrentUserRoles();
  const isServiceProvider =
    roles.length > 0 &&
    roles.some((role) => role === UserRoles.SERVICE_PROVIDER);
  const isMarinaManager =
    roles.length > 0 && roles.some((role) => role === UserRoles.MARINA);
  const isRegionalOperator =
    roles.length > 0 &&
    roles.some((role) => role === UserRoles.REGIONAL_OPERATOR);

  const toolbarDescription = useMemo(
    () =>
      getToolbarDescription(
        isServiceProvider,
        isMarinaManager,
        isRegionalOperator
      ),
    [isServiceProvider, isMarinaManager, isRegionalOperator]
  );

  const dashboardContent = useMemo(() => {
    if (isServiceProvider) {
      return <ServiceProviderDashboard />;
    }
    if (isMarinaManager) {
      return <MarinaManagerDashboard />;
    }
    if (isRegionalOperator) {
      return <RegionalOperatorDashboard />;
    }
    return <Demo1LightSidebarContent />;
  }, [isServiceProvider, isMarinaManager, isRegionalOperator]);

  return (
    <Fragment>
      <Container>
        <Toolbar>
          <ToolbarHeading
            id="dashboard-heading"
            title="Dashboard"
            description={toolbarDescription}
          />
          <ToolbarActions>
            <Link
              to="/account/home/user-profile"
              className="btn btn-sm btn-light"
            >
              View Profile
            </Link>
          </ToolbarActions>
        </Toolbar>
      </Container>

      <Container>{dashboardContent}</Container>
    </Fragment>
  );
};

export { Demo1LightSidebarPage };
